import useBacktestStore from '../Store/backtestStore';
import {
  ResponsiveContainer,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Area,
  ComposedChart
} from 'recharts';

/* ---------- helpers ---------- */

function num(v, opts = {}) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, opts);
}

function money(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
}

function pct(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(2)}%`;
}

function dateOnly(s) {
  if (!s) return '—';
  return String(s).slice(0, 10);
}

function formatTooltipDate(value) {
  if (!value) return '—';

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const yyyy = raw.slice(0, 4);
    const mm = raw.slice(5, 7);
    const dd = raw.slice(8, 10);
    return `${mm}/${dd}/${yyyy.slice(2)}`;
  }

  return raw;
}

function formatXAxisMonthYear(value) {
  if (value === null || value === undefined) return '';

  const raw = String(value).trim();

  const monthByNumber = {
    '01': 'Jan',
    '02': 'Feb',
    '03': 'Mar',
    '04': 'Apr',
    '05': 'May',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Aug',
    '09': 'Sep',
    '10': 'Oct',
    '11': 'Nov',
    '12': 'Dec'
  };

  const monthByName = {
    Jan: 'Jan',
    Feb: 'Feb',
    Mar: 'Mar',
    Apr: 'Apr',
    May: 'May',
    Jun: 'Jun',
    Jul: 'Jul',
    Aug: 'Aug',
    Sep: 'Sep',
    Oct: 'Oct',
    Nov: 'Nov',
    Dec: 'Dec'
  };

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const year = raw.slice(0, 4);
    const month = raw.slice(5, 7);
    return `${monthByNumber[month] ?? month} ${year}`;
  }

  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(raw)) {
    const [m, , y] = raw.split('/');
    const year = y.length === 2 ? `20${y}` : y;
    const month = String(m).padStart(2, '0');
    return `${monthByNumber[month] ?? month} ${year}`;
  }

  const parts = raw.split(/\s+/);
  if (parts.length >= 3 && monthByName[parts[1]]) {
    const month = monthByName[parts[1]];
    const yearPart = parts.find((p) => /^\d{4}$/.test(p));

    if (yearPart) {
      return `${month} ${yearPart}`;
    }

    return month;
  }

  return raw;
}

function getValidEquity(rows) {
  return rows
    .map((r) => Number(r.equity))
    .filter(Number.isFinite);
}

function getMaxDrawdown(rows) {
  let peak = -Infinity;
  let maxDrawdown = 0;
  let maxDrawdownPct = 0;

  for (const row of rows) {
    const equity = Number(row.equity);
    if (!Number.isFinite(equity)) continue;

    peak = Math.max(peak, equity);

    const drawdown = equity - peak;
    const drawdownPct = peak > 0 ? drawdown / peak : 0;

    if (drawdown < maxDrawdown) {
      maxDrawdown = drawdown;
      maxDrawdownPct = drawdownPct;
    }
  }

  return {
    maxDrawdown,
    maxDrawdownPct
  };
}

/* ---------- tooltip ---------- */

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row) return null;

  const equity = row.equity;
  const rawEquity = row.raw_equity;
  const dailyPnl = row.daily_pnl;
  const isLive = row.source === 'LIVE';

  return (
    <div
      style={{
        minWidth: 220,
        background:
          'linear-gradient(180deg, rgba(18,22,32,0.98), rgba(8,10,15,0.98))',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 14,
        boxShadow: '0 18px 45px rgba(0,0,0,0.55)',
        color: 'rgba(255,255,255,0.94)',
        padding: '12px 14px'
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.52)',
          marginBottom: 7
        }}
      >
        {formatTooltipDate(label)} • {row.period}
      </div>

      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          marginBottom: 10
        }}
      >
        {money(equity)}
      </div>

      <div
        style={{
          display: 'grid',
          gap: 6,
          fontSize: 12
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18 }}>
          <span style={{ color: 'rgba(255,255,255,0.55)' }}>Realized PnL</span>
          <span
            style={{
              fontWeight: 700,
              color:
                Number(dailyPnl) > 0
                  ? '#37d67a'
                  : Number(dailyPnl) < 0
                    ? '#ff5c7a'
                    : 'rgba(255,255,255,0.85)'
            }}
          >
            {money(dailyPnl)}
          </span>
        </div>

        {isLive && Number.isFinite(Number(rawEquity)) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18 }}>
            <span style={{ color: 'rgba(255,255,255,0.55)' }}>Raw Live Equity</span>
            <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
              {money(rawEquity)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- main chart ---------- */

export default function EquityChart() {
  const equitySeries = useBacktestStore((s) => s.equitySeries);

  if (!equitySeries?.length) {
    return <div className="mutedLine">No equity data yet.</div>;
  }

  const STARTING_EQUITY = 20000;

  const normalizedSeries = equitySeries.map((r) => {
    const source = String(r.source || '').trim().toUpperCase();
    const rawEquity = Number(r.equity);

    return {
      ...r,
      source,
      raw_equity: Number.isFinite(rawEquity) ? rawEquity : null
    };
  });

  const firstLiveIndex = normalizedSeries.findIndex((r) => r.source === 'LIVE');

  const lastBacktestBeforeLive =
    firstLiveIndex > 0
      ? normalizedSeries
          .slice(0, firstLiveIndex)
          .reverse()
          .find((r) => r.source !== 'LIVE' && Number.isFinite(Number(r.raw_equity)))
      : null;

  const firstLivePoint =
    firstLiveIndex >= 0 && Number.isFinite(Number(normalizedSeries[firstLiveIndex]?.raw_equity))
      ? normalizedSeries[firstLiveIndex]
      : null;

  const lastBacktestEquity = Number(lastBacktestBeforeLive?.raw_equity);
  const firstLiveRawEquity = Number(firstLivePoint?.raw_equity);

  /*
    Correct rebase:
    First live point should begin at the last backtest equity.
    Every later live point should reflect only the live equity CHANGE
    since live tracking began.

    displayLiveEquity = lastBacktestEquity + (rawLiveEquity - firstLiveRawEquity)
  */
  const displaySeries = normalizedSeries.map((r) => {
    const rawEquity = Number(r.raw_equity);
    const isLive = r.source === 'LIVE';

    let displayEquity = Number.isFinite(rawEquity) ? rawEquity : null;

    if (
      isLive &&
      Number.isFinite(rawEquity) &&
      Number.isFinite(lastBacktestEquity) &&
      Number.isFinite(firstLiveRawEquity)
    ) {
      displayEquity = lastBacktestEquity + (rawEquity - firstLiveRawEquity);
    }

    return {
      ...r,
      equity: displayEquity,
      raw_equity: rawEquity
    };
  });

  const first = displaySeries[0];
  const last = displaySeries[displaySeries.length - 1];

  const allEquityVals = getValidEquity(displaySeries);

  const dataMin = allEquityVals.length
    ? Math.min(...allEquityVals)
    : STARTING_EQUITY;

  const dataMax = allEquityVals.length
    ? Math.max(...allEquityVals)
    : STARTING_EQUITY;

  const yPadding = Math.max((dataMax - dataMin) * 0.16, 1500);
  const yMin = Math.max(0, Math.floor((dataMin - yPadding) / 1000) * 1000);
  const yMax = Math.ceil((dataMax + yPadding) / 1000) * 1000;

  const endingEquity = Number(last.equity);
  const netProfit = endingEquity - STARTING_EQUITY;
  const totalReturnPct =
    STARTING_EQUITY > 0 ? (netProfit / STARTING_EQUITY) * 100 : null;

  const wins = displaySeries.filter((r) => {
    const pnl =
      r.daily_pnl !== null && r.daily_pnl !== undefined
        ? Number(r.daily_pnl)
        : r.realized_pnl !== null && r.realized_pnl !== undefined
          ? Number(r.realized_pnl)
          : null;

    return Number(pnl) > 0;
  }).length;

  const losses = displaySeries.filter((r) => {
    const pnl =
      r.daily_pnl !== null && r.daily_pnl !== undefined
        ? Number(r.daily_pnl)
        : r.realized_pnl !== null && r.realized_pnl !== undefined
          ? Number(r.realized_pnl)
          : null;

    return Number(pnl) < 0;
  }).length;

  const closedTrades = wins + losses;
  const winRate = closedTrades > 0 ? (wins / closedTrades) * 100 : null;

  const { maxDrawdown, maxDrawdownPct } = getMaxDrawdown(displaySeries);

  const baseChartData = displaySeries.map((r) => {
    const d = dateOnly(r.date);
    const isLive = r.source === 'LIVE';
    const equity = Number(r.equity);
    const rawEquity = Number(r.raw_equity);

    const dailyPnl =
      r.daily_pnl !== null && r.daily_pnl !== undefined
        ? Number(r.daily_pnl)
        : r.realized_pnl !== null && r.realized_pnl !== undefined
          ? Number(r.realized_pnl)
          : null;

    const dailyReturn =
      r.daily_return !== null && r.daily_return !== undefined
        ? Number(r.daily_return)
        : null;

    return {
      date: d,

      equity,
      equity_fill: equity,

      raw_equity: rawEquity,

      equity_backtest: isLive ? null : equity,
      equity_live: isLive ? equity : null,

      daily_pnl: dailyPnl,
      daily_return: dailyReturn,

      source: r.source,
      period: isLive ? 'LIVE' : 'BACKTEST'
    };
  });

  const liveStartIndex = baseChartData.findIndex((r) => r.source === 'LIVE');

  if (liveStartIndex > 0) {
    const priorBacktestPoint = baseChartData[liveStartIndex - 1];

    baseChartData[liveStartIndex - 1] = {
      ...priorBacktestPoint,
      equity_live: priorBacktestPoint.equity,
      period: 'BACKTEST_TO_LIVE'
    };
  }

  const chartData = baseChartData;

  const liveStartDate =
    liveStartIndex >= 0 ? baseChartData[liveStartIndex].date : null;

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 24,
        border: '1px solid rgba(255,255,255,0.10)',
        background:
          'radial-gradient(circle at 15% 0%, rgba(177,136,0,0.18), transparent 34%), radial-gradient(circle at 85% 12%, rgba(255,255,255,0.08), transparent 28%), linear-gradient(180deg, rgba(15,18,27,0.98), rgba(5,7,11,0.98))',
        boxShadow:
          '0 24px 70px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
        padding: 22
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 'auto -80px -130px auto',
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: 'rgba(177,136,0,0.12)',
          filter: 'blur(45px)',
          pointerEvents: 'none'
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 18,
          alignItems: 'flex-start',
          marginBottom: 18
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.52)',
              fontWeight: 800,
              marginBottom: 7
            }}
          >
            ARGOS Performance Curve
          </div>

          <div
            style={{
              fontSize: 28,
              lineHeight: 1.05,
              fontWeight: 900,
              color: 'rgba(255,255,255,0.96)'
            }}
          >
            {money(endingEquity)}
          </div>

          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              color: 'rgba(255,255,255,0.58)'
            }}
          >
            {formatTooltipDate(first.date)} → {formatTooltipDate(last.date)} •{' '}
            {num(displaySeries.length)} closed trades
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}
        >
          <div className="miniStatModern">
            <div className="miniLabelModern">Start</div>
            <div className="miniValueModern">{money(STARTING_EQUITY)}</div>
          </div>

          <div className="miniStatModern">
            <div className="miniLabelModern">Net Profit</div>
            <div
              className="miniValueModern"
              style={{
                color: netProfit >= 0 ? '#37d67a' : '#ff5c7a'
              }}
            >
              {money(netProfit)}
            </div>
          </div>

          <div className="miniStatModern">
            <div className="miniLabelModern">Return</div>
            <div
              className="miniValueModern"
              style={{
                color: Number(totalReturnPct) >= 0 ? '#37d67a' : '#ff5c7a'
              }}
            >
              {pct(totalReturnPct)}
            </div>
          </div>

          <div className="miniStatModern">
            <div className="miniLabelModern">Win Rate</div>
            <div className="miniValueModern">{pct(winRate)}</div>
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
          marginBottom: 16
        }}
      >
        <div className="metricTile">
          <span>Wins</span>
          <strong>{num(wins)}</strong>
        </div>

        <div className="metricTile">
          <span>Losses</span>
          <strong>{num(losses)}</strong>
        </div>

        <div className="metricTile">
          <span>Max Drawdown</span>
          <strong style={{ color: '#ff5c7a' }}>{money(maxDrawdown)}</strong>
        </div>

        <div className="metricTile">
          <span>Drawdown %</span>
          <strong style={{ color: '#ff5c7a' }}>{pct(maxDrawdownPct * 100)}</strong>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          height: 390,
          borderRadius: 18,
          border: '1px solid rgba(255,255,255,0.08)',
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))',
          padding: '12px 8px 4px 0'
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 18, right: 30, left: 8, bottom: 8 }}
          >
            <defs>
              <linearGradient id="argosEquityFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#B18800" stopOpacity={0.28} />
                <stop offset="55%" stopColor="#B18800" stopOpacity={0.08} />
                <stop offset="100%" stopColor="#B18800" stopOpacity={0} />
              </linearGradient>

              <linearGradient id="argosLiveLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#B18800" />
                <stop offset="100%" stopColor="#F1D27A" />
              </linearGradient>

              <linearGradient id="argosBacktestLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="rgba(255,255,255,0.44)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.90)" />
              </linearGradient>
            </defs>

            <CartesianGrid
              stroke="rgba(255,255,255,0.07)"
              strokeDasharray="4 8"
              vertical={false}
            />

            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              height={38}
              minTickGap={36}
              interval="preserveStartEnd"
              tick={{
                fontSize: 11,
                fill: 'rgba(255,255,255,0.42)'
              }}
              tickFormatter={formatXAxisMonthYear}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 11,
                fill: 'rgba(255,255,255,0.42)'
              }}
              tickFormatter={(v) => `$${num(v)}`}
              width={78}
              domain={[yMin, yMax]}
              allowDataOverflow
            />

            <ReferenceLine
              y={STARTING_EQUITY}
              stroke="rgba(255,255,255,0.24)"
              strokeDasharray="5 5"
              strokeWidth={1}
              label={{
                value: 'Starting Equity',
                position: 'insideTopLeft',
                fill: 'rgba(255,255,255,0.42)',
                fontSize: 11,
                fontWeight: 700
              }}
            />

            {liveStartDate && (
              <ReferenceLine
                x={liveStartDate}
                stroke="rgba(241,210,122,0.65)"
                strokeDasharray="6 6"
                strokeWidth={1.3}
                label={{
                  value: 'Live Start',
                  position: 'insideTopRight',
                  fill: 'rgba(241,210,122,0.9)',
                  fontSize: 11,
                  fontWeight: 800
                }}
              />
            )}

            <Tooltip
              cursor={{
                stroke: 'rgba(241,210,122,0.35)',
                strokeWidth: 1,
                strokeDasharray: '4 4'
              }}
              content={<CustomTooltip />}
            />

            <Area
              type="monotone"
              dataKey="equity_fill"
              fill="url(#argosEquityFill)"
              stroke="transparent"
              connectNulls={false}
              isAnimationActive={false}
            />

            <Line
              type="monotone"
              dataKey="equity_backtest"
              dot={false}
              activeDot={{
                r: 4,
                stroke: 'rgba(255,255,255,0.9)',
                strokeWidth: 2,
                fill: '#0f1219'
              }}
              strokeWidth={2}
              stroke="url(#argosBacktestLine)"
              connectNulls={false}
              isAnimationActive={false}
            />

            <Line
              type="monotone"
              dataKey="equity_live"
              dot={false}
              activeDot={{
                r: 5,
                stroke: '#F1D27A',
                strokeWidth: 2,
                fill: '#0f1219'
              }}
              strokeWidth={2.75}
              stroke="url(#argosLiveLine)"
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 12,
          alignItems: 'center',
          marginTop: 13,
          color: 'rgba(255,255,255,0.48)',
          fontSize: 12
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
            <span
              style={{
                width: 18,
                height: 3,
                borderRadius: 99,
                background: 'rgba(255,255,255,0.76)',
                display: 'inline-block'
              }}
            />
            Backtest
          </div>

          <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
            <span
              style={{
                width: 18,
                height: 3,
                borderRadius: 99,
                background: '#B18800',
                display: 'inline-block'
              }}
            />
            Live
          </div>
        </div>
      </div>

      <style>{`
        .miniStatModern {
          min-width: 108px;
          padding: 10px 12px;
          border-radius: 15px;
          border: 1px solid rgba(255,255,255,0.09);
          background: rgba(255,255,255,0.045);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
        }

        .miniLabelModern {
          font-size: 10px;
          letter-spacing: 0.11em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.42);
          font-weight: 800;
          margin-bottom: 5px;
        }

        .miniValueModern {
          font-size: 15px;
          font-weight: 850;
          color: rgba(255,255,255,0.92);
        }

        .metricTile {
          padding: 11px 12px;
          border-radius: 15px;
          border: 1px solid rgba(255,255,255,0.075);
          background: rgba(0,0,0,0.18);
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: center;
        }

        .metricTile span {
          font-size: 11px;
          color: rgba(255,255,255,0.44);
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .metricTile strong {
          font-size: 14px;
          color: rgba(255,255,255,0.9);
          font-weight: 850;
        }

        @media (max-width: 820px) {
          .metricTile {
            align-items: flex-start;
            flex-direction: column;
            gap: 5px;
          }
        }

        @media (max-width: 680px) {
          .miniStatModern {
            min-width: calc(50% - 5px);
          }
        }
      `}</style>
    </div>
  );
}

